/**
 * Find a tour target element by CSS selector.
 * Handles AG Grid's split DOM containers by searching both the main document
 * and any AG Grid pinned/center column containers.
 */
export function findTourTarget(selector: string): HTMLElement | null {
  // Try direct querySelector first
  const el = document.querySelector<HTMLElement>(selector);
  if (el) return el;

  // AG Grid renders pinned columns in separate containers - search those too
  const agGridContainers = document.querySelectorAll(
    '.ag-pinned-left-cols-container, .ag-pinned-right-cols-container, .ag-center-cols-container, .ag-body-viewport'
  );
  for (const container of agGridContainers) {
    const found = container.querySelector<HTMLElement>(selector);
    if (found) return found;
  }

  return null;
}

/**
 * Wait for an element matching the selector to appear in the DOM.
 * Uses MutationObserver for efficiency. Resolves with the element or rejects on timeout.
 */
export function waitForElement(selector: string, timeoutMs = 5000): Promise<HTMLElement> {
  return new Promise((resolve, reject) => {
    // Check if already present
    const existing = findTourTarget(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const timer = setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Tour: waitForElement timed out for selector "${selector}"`));
    }, timeoutMs);

    const observer = new MutationObserver(() => {
      const el = findTourTarget(selector);
      if (el) {
        clearTimeout(timer);
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
}

// ---------------------------------------------------------------------------
// Tour action helpers (reusable across tour configs)
// ---------------------------------------------------------------------------

/**
 * Pause for a given number of milliseconds.
 */
export const pause = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Simulate a click on a target element found by CSS selector.
 */
export const clickTarget = async (selector: string): Promise<void> => {
  const el = findTourTarget(selector);
  if (el) {
    el.click();
    await pause(200);
  }
};

/**
 * Check if an MUI Checkbox wrapper element is currently checked.
 * MUI renders data-cy on the outer <span>, not the <input>, so we
 * need to look inside for the actual input to read .checked.
 */
export const isMuiCheckboxChecked = (wrapperSelector: string): boolean => {
  const wrapper = findTourTarget(wrapperSelector);
  if (!wrapper) return false;
  const input = wrapper.querySelector<HTMLInputElement>('input[type="checkbox"]');
  return input?.checked ?? false;
};

/**
 * Close a MUI Dialog by clicking its close element (a div with role="button" aria-label="close").
 * Falls back to clicking the MUI backdrop.
 */
export const closeMuiDialog = async (cypressId: string): Promise<void> => {
  const dialog = document.querySelector(`[data-cy="${cypressId}"]`);
  if (dialog) {
    const closeBtn = dialog.querySelector<HTMLElement>('[aria-label="close"]');
    if (closeBtn) {
      closeBtn.click();
      await pause(300);
      return;
    }
  }
  const backdrop = document.querySelector<HTMLElement>('.MuiDialog-root .MuiBackdrop-root');
  if (backdrop) {
    backdrop.click();
    await pause(300);
  }
};

/**
 * Close a MUI Popover by clicking its backdrop.
 */
export const closeMuiPopover = async (): Promise<void> => {
  const backdrop = document.querySelector<HTMLElement>('.MuiPopover-root .MuiBackdrop-root');
  if (backdrop) {
    backdrop.click();
    await pause(200);
  }
};

/**
 * Smoothly scroll the element into view if it's not currently visible in the viewport.
 */
export function scrollIntoViewIfNeeded(el: HTMLElement): void {
  const rect = el.getBoundingClientRect();
  const isVisible =
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth;

  if (!isVisible) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }
}
